import React, { useCallback, useEffect, useMemo, useState } from "react";

import AutocompleteSelectInput from "veneer/AutocompleteSelectInput";
import Stack from "veneer/Stack";
import TextInput from "veneer/TextInput";

import type { Media } from "cortex/ui/src/fleet/view/media-library/model";
import Notification from "cortex/ui/src/ui/notification";
import type { Option } from "cortex/ui/src/ui/table/types";
import { configReducer } from "cortex/ui/src/util/array";
import { arrayToSelectOptions } from "cortex/ui/src/util/select";
import { utcToLocal } from "cortex/ui/src/util/time";

import { type EditContentFormState, type GetChildState } from "./sidebarTypes";

interface EditContentFormProps {
  categories?: ReadonlyArray<string>;
  excludeFields?: ReadonlyArray<string>;
  getState?: GetChildState;
  mediaEntry?: Partial<Media>;
  readOnlyFields?: ReadonlyArray<string>;
}

type FieldConfig = Record<string, boolean>;

function getOptions(values?: ReadonlyArray<string> | null) {
  return Array.isArray(values)
    ? values.map((value) => ({ label: value, value }))
    : null;
}

function getFormState(mediaEntry: Partial<Media> = {}): EditContentFormState {
  const {
    conflictsWith,
    duration: durationMs,
    id,
    name,
    tag,
    type,
    upload_date: uploadDate,
    url,
  } = mediaEntry;

  return {
    duration: durationMs ? durationMs / 1000 : 0,
    id,
    name,
    tag,
    type,
    uploadDate,
    url,
    conflictsWith: getOptions(conflictsWith),
  };
}

export function EditContentForm({
  categories = [],
  excludeFields = [],
  getState = () => {},
  mediaEntry,
  readOnlyFields = [],
}: EditContentFormProps) {
  const [formState, setFormState] = useState(() =>
    getFormState(mediaEntry ?? {}),
  );

  useEffect(() => {
    setFormState(getFormState(mediaEntry ?? {}));
  }, [mediaEntry]);

  const exportState = useCallback(() => {
    const { conflictsWith, duration: durationSecs } = formState;
    return {
      ...formState,
      conflictsWith: conflictsWith ? conflictsWith.map((cw) => cw.value) : null,
      duration: Number(durationSecs) * 1000,
    };
  }, [formState]);

  useEffect(() => {
    getState(EditContentForm.name, exportState);
  }, [exportState, getState]);

  const exclude = useMemo(
    () => excludeFields.reduce(configReducer, {} as FieldConfig),
    [excludeFields],
  );
  const readOnly = useMemo(
    () => readOnlyFields.reduce(configReducer, {} as FieldConfig),
    [readOnlyFields],
  );
  const categoryOptions = useMemo(
    () => arrayToSelectOptions([...categories]) as Option[],
    [categories],
  );
  const readOnlyForm = readOnly.all;

  const updateField = (field: keyof EditContentFormState, value: unknown) => {
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const isReadOnly = (field: string) =>
    Boolean(readOnlyForm || readOnly[field]);

  return (
    <Stack gap={2}>
      <Notification />
      {!exclude.name && (
        <TextInput
          disabled={isReadOnly("name")}
          fullWidth
          label="Name"
          onChange={(value) => updateField("name", value)}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
          placeholder="Name"
          required
          size="medium"
          value={formState.name ?? ""}
        />
      )}
      {!exclude.uploadDate && (
        <TextInput
          disabled
          fullWidth
          label="Upload Date"
          onChange={() => {}}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
          size="medium"
          value={utcToLocal(formState.uploadDate)}
        />
      )}
      {!exclude.url && (
        <TextInput
          disabled={isReadOnly("url")}
          fullWidth
          label="URL"
          onChange={(value) => updateField("url", value)}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
          placeholder="https://www.vistarmedia.com"
          required
          size="medium"
          value={formState.url ?? ""}
        />
      )}
      {!exclude.duration && (
        <TextInput
          disabled={isReadOnly("duration")}
          fullWidth
          label="Duration (in seconds)"
          onChange={(value) => updateField("duration", value)}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
          placeholder="Duration (seconds)"
          required
          size="medium"
          value={`${formState.duration ?? ""}`}
        />
      )}
      {!exclude.category && (
        <AutocompleteSelectInput<Option, false, false, true>
          disabled={isReadOnly("category")}
          freeSolo
          inputProps={{ label: "Creative Category" }}
          onChange={(_event, value) => {
            updateField(
              "tag",
              typeof value === "string" ? value : (value?.value ?? null),
            );
          }}
          options={categoryOptions}
          value={
            formState.tag
              ? { label: formState.tag, value: formState.tag }
              : null
          }
        />
      )}
      {!exclude.conflictsWith && (
        <AutocompleteSelectInput<Option, true, false, true>
          disabled={isReadOnly("conflictsWith")}
          freeSolo
          inputProps={{ label: "Creative Category Restrictions" }}
          multiple
          onChange={(_event, value) => {
            updateField(
              "conflictsWith",
              value.map((option) =>
                typeof option === "string"
                  ? { label: option, value: option }
                  : option,
              ),
            );
          }}
          options={categoryOptions}
          value={[...(formState.conflictsWith ?? [])]}
        />
      )}
    </Stack>
  );
}
